// tslint:disable:variable-name
import { Proto } from "@8x8/chalet-proto";
import { combineEpics } from "redux-observable";

import { actionCreators } from "../actions";
import { PULSE_MILLISECONDS } from "../app/Config";
import { rootEpic } from "../epics";
import { getDiscoverUrl, getToken, restore } from "../reducers/persist";
import { getActiveRoomView, getFlattenRoomViews, getRoomMentionsIsOpen } from "../reducers/rooms";
import { ProtoProxy } from "../service/ProtoProxy";
import { cycleOverList } from "../service/Utils";
import { bootstrapStore } from "../utils/bootstrap";
import * as BootstrapModule from "../utils/bootstrap.dev";
import { discoverUrl } from "../utils/config";
import { RootStore } from "../utils/types";

import { AccessStore } from "./stores/AccessStore";
import { MediaViewerStore } from "./stores/MediaViewerStore";
import { RoomsStore } from "./stores/RoomsStore";

export class App {
    constructor() {
        let ls: typeof localStorage | undefined;
        try {
            ls = localStorage;
        } catch (e) {}
        const { store, actions, epicMiddleware } = (process.env.NODE_ENV === "production"
            ? bootstrapStore
            : (require("../utils/bootstrap.dev") as typeof BootstrapModule).bootstrapStoreDev)(
            { App: this, Image },
            rootEpic,
            restore(ls)
        );
        this._rootStore = store;
        this._actionCreators = actions;
        this._protoProxy = new ProtoProxy(this);
        this._proto = this.setupProto();
        if (
            window.location.search.match("\\?page=admin") === null &&
            window.location.search.match("\\?page=client-signin") === null
        ) {
            document.addEventListener("keydown", this._onGlobalKeyDown);
        }
        epicMiddleware.run(combineEpics(
            this.accessStore.epic,
            this.mediaViewerStore.epic,
            this.roomsStore.epic,
            this.protoProxy.epic
        ));
        const token = getToken(this.rootStore.getState());
        this.actionCreators.appIsReady();
        if (token) {
            this.actionCreators.setProtoToken({ token });
        }
    }

    private _rootStore: RootStore;
    private _actionCreators: typeof actionCreators;

    private _config = { PULSE_MILLISECONDS, DISCOVER_URL: discoverUrl, WEBSOCKET_CLASS: typeof WebSocket === "function" ? WebSocket: undefined, useDiscover: true };
    private _proto: Proto;
    private _protoProxy: ProtoProxy;

    private _roomsStore = new RoomsStore(this);
    private _accessStore = new AccessStore(this);
    private _mediaViewerStore = new MediaViewerStore();

    public get roomsStore() { return this._roomsStore; }
    public get accessStore() { return this._accessStore; }
    public get mediaViewerStore() { return this._mediaViewerStore; }

    public get rootStore() { return this._rootStore; }
    public get actionCreators() { return this._actionCreators; }
    public get proto() { return this._proto; }
    public get protoProxy() { return this._protoProxy; }

    public setupProto() {
        this._config.DISCOVER_URL = getDiscoverUrl(this.rootStore.getState())
        this._proto = new Proto(this._config);
        // this._proto.setDebugOptions({ wsLatency: 1000, wsSendDelay: 1000, wsReceiveDelay: 1000 }); // let the whole world burn
        // this._proto.setDebugOptions({ wsLatency: 100, wsSendDelay: 100, wsReceiveDelay: 100 }); // slightly slower than prod
        this.proto.setProxy(this.protoProxy);
        return this.proto;
    }

    public test(test = 1, n = 10) {
        let output: any;
        if (process.env.NODE_ENV !== "production") {
            console.time('redux');
            for (let i = 0; i < n; ++i) {
                const value = i % 2 === 0;
                const monolog = { "type": "roster" as const, "rooms": [{ "type": "monolog" as const, "name": "Yaroslav Lapin" + i, "monolog": { "location": "United States - San Jose/Headquarters", "last_seen": { "tz": -480, "ts": 1551664898448 }, "last_name": "Lapin", "job_title": "Software Engineer", "id": "260411699471614518665816111236", "first_name": "Yaroslav", "external_login_id": "8X8.2576", "email": "Yaroslav.Lapin@8x8.com", "department": "Product Management & Operations (4150)", "avatar_url": "https://avatars-cdn.us-east.chalet.8x8.com/252509770621135977884589294032/260411699471614518665816111236.png" }, "id": "m:MjYwNDExNjk5NDcxNjE0NTE4NjY1ODE2MTExMjM2", "created_ts": 1495740984700244 }] }
                const typing = {
                    type: 'ON_TYPING',
                    payload: {
                        user_ids: value ? ['260411699471614518665816111236'] : [],
                        type: 'typing',
                        room_id: 'f:j796BNDX3SB6OwH4j-VqeHzVfSuE6FDR7SBD8GuqBK8'
                    }
                }
                const rosterSearch = { type: value ? 'ROSTER_SEARCH_OFF' : 'ROSTER_SEARCH_ON' };
                switch (test) {
                    case 1:
                        this.actionCreators.onRoster({ data: monolog });
                        break;
                    case 2:
                        this.rootStore.dispatch(rosterSearch);
                        break;
                    case 3:
                        this.rootStore.dispatch(typing);
                        break;
                        case 4:
                        output = (() => {
                            const el = document.querySelectorAll('body *');
                            const depths = new Map();
                            depths.set(document.body, -1)
                            el.forEach((e) => {
                                const p = e.parentNode;
                                const d = depths.get(p);
                                depths.set(e, d + 1);
                            })
                            return depths;
                        })()

                        break;
                        case 5:
                        output = (() => {
                            const el = document.querySelectorAll('body *');
                            const childCount = new Map();
                            el.forEach((e) => {
                                const c = e.childElementCount;
                                childCount.set(e, c);
                            })
                            return childCount;
                        })()

                        break;
                }
            }
            console.timeEnd('redux');
            return output;
        }
    }

    private _keys = {
        backSpace: 8,
        tab: 9,
        enter: 13,
        shift: 16,
        pageUp: 33,
        pageDown: 34,
        end: 35,
        home: 36,
        left: 37,
        up: 38,
        right: 39,
        down: 40,
    };

    private _onGlobalKeyDown = (e: KeyboardEvent) => {
        const keys = this._keys;
        const element = document.activeElement;

        if (e.which === keys.tab) {
            if (element && element instanceof HTMLElement && !element.classList.contains("tab-cycle")) {
                const name = element.nodeName.toLowerCase();
                if (name === "input" || name === "textarea") {
                    e.preventDefault();
                    e.stopPropagation();
                    return
                }
            }

            e.preventDefault();

            const store = this.rootStore.getState();
            const roomViews = getFlattenRoomViews(store);
            const activeView = getActiveRoomView(store);
            const nextView = cycleOverList(roomViews.toArray(), activeView, e.shiftKey ? "prev" : "next");
            if (nextView) {
                if (activeView && getRoomMentionsIsOpen(store, {roomView: activeView})) {
                    e.preventDefault();
                    return
                }
                this.actionCreators.setRoomViewAsActive({ roomView: nextView, focus: true });
                return
            }
        }


        if (element && element instanceof HTMLElement) {
            if (element.contentEditable === "true") {
                return // we are already have some of intput fields in focus
            }

            const name = element.nodeName.toLowerCase();
            if (name === "input" || name === "textarea") {
                return // some other input already has focus
            }
        }

        const pageOrHome = e.which === keys.pageUp || e.which === keys.pageDown || e.which === keys.home || e.which === keys.end;
        if (pageOrHome) {
            // TODO: pass to the scroll page history
            return
        }

        const arrowKeys = e.which === keys.down || e.which === keys.up || e.which === keys.left || e.which === keys.right;

        if (arrowKeys) {
            return // ignore arrow keys
        }

        const hasAnyModalOpened = false; // TODO: add checks
        if (hasAnyModalOpened) {
            return
        }

        if (e.which === keys.backSpace || e.which === keys.enter) {
            return // FIXME: remove this when drift-js stops crashing on this
        }

        if (e.ctrlKey || e.metaKey || e.altKey || e.which === keys.shift) {
            return // ignore special keys, but you can still do uppercase which is shift + symbol
        }

        const activeView = getActiveRoomView(this.rootStore.getState());
        if (activeView) {
            this.actionCreators.setRoomViewAsActive({ roomView: activeView, focus: true });
        }
    }
}

if (module.hot) {
    module.hot.decline();
}
